import type { ChannelsSummary } from '@qlk/shared';
import { Link } from 'react-router-dom';

import { Avatar, Money, StatusBadge } from '../../../ui/index.js';
import { phanTram } from './soLieu.js';

/**
 * Tỷ trọng doanh thu từng kênh. Vẽ bằng thanh CSS thuần,
 * không thêm thư viện biểu đồ (xem docs/modules/channels.md mục Quyết định).
 */
/**
 * `duLieu === undefined` nghĩa **"bạn không được xem tiền"** (mục S5: máy chủ
 * bỏ hẳn `revenueShare`), khác hẳn mảng rỗng nghĩa "chưa kênh nào có doanh
 * thu". Trang gọi component này đã không render nó với người thiếu quyền; nhánh
 * ở đây là lớp thứ hai, và nó **không** vẽ khung trống để không ai hỏi "sao
 * khung này rỗng".
 */
export function BieuDoTyTrong({ duLieu }: { duLieu: ChannelsSummary['revenueShare'] }) {
  if (duLieu === undefined) return null;

  if (duLieu.length === 0) {
    return (
      <section className="rounded-the border border-vien bg-be-mat p-6">
        <h2 className="text-tieu-de-khoi font-semibold text-chu">Tỷ trọng doanh thu</h2>
        <p className="mt-2 text-co-ban text-chu-phu">Chưa có kênh nào phát sinh doanh thu.</p>
      </section>
    );
  }

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="text-tieu-de-khoi font-semibold text-chu">Tỷ trọng doanh thu</h2>
      <ul className="mt-4 flex flex-col gap-4">
        {duLieu.map((d) => (
          <li key={d.channelId}>
            <div className="flex items-center justify-between gap-3 text-co-ban">
              <Link to={`/kenh/${d.channelId}`} className="truncate text-chu hover:text-nhan">
                {d.name}
              </Link>
              <span className="so-lieu shrink-0 text-chu-phu">
                <Money value={d.grossRevenue} /> · {phanTram(d.percent)}
              </span>
            </div>
            <div
              className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-nen"
              role="img"
              aria-label={`${d.name}: ${phanTram(d.percent)} doanh thu gộp`}
            >
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${String(Math.max(d.percent, 1))}%`,
                  backgroundColor: d.avatarColor,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Thẻ "Kênh chưa có doanh thu" bên cạnh biểu đồ. */
export function TheKenhChuaCoDoanhThu({
  duLieu,
}: {
  duLieu: ChannelsSummary['channelsWithoutRevenue'];
}) {
  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="text-tieu-de-khoi font-semibold text-chu">
        Kênh chưa có doanh thu <span className="so-lieu text-chu-mo">({duLieu.length})</span>
      </h2>

      {duLieu.length === 0 ? (
        <p className="mt-2 text-co-ban text-chu-phu">Mọi kênh đều đang có doanh thu.</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {duLieu.map((d) => (
            <li key={d.channelId} className="flex items-center gap-3">
              <Avatar name={d.name} color={d.avatarColor} size={32} />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/kenh/${d.channelId}`}
                  className="block truncate text-co-ban font-medium text-chu hover:text-nhan"
                >
                  {d.name}
                </Link>
                <p className="truncate text-phu text-chu-mo">{d.niche}</p>
              </div>
              <StatusBadge tone={d.monetizationStatus === 'under_review' ? 'warn' : 'neutral'}>
                {d.monetizationStatus === 'under_review' ? 'Đang xét duyệt' : 'Chưa đủ điều kiện'}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
